import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import PollComponent from './PollComponent';
import TableComponent from './TableComponent';
import MapComponent from './MapComponent';
import VibeResultCard from './VibeResultCard';

const ChecklistItem = ({ item }) => {
    const [checked, setChecked] = useState(false);
    const label = typeof item === 'object' ? item.label : item;
    const description = typeof item === 'object' ? item.description : null;

    return (
        <label className="flex items-start py-1.5 cursor-pointer group">
            <div className="flex-shrink-0 mt-0.5">
                <div className="relative flex items-center justify-center w-5 h-5">
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                        className="peer appearance-none w-5 h-5 border-2 border-[var(--border-subtle)] rounded-md checked:bg-[var(--accent-primary)] checked:border-[var(--accent-primary)] transition-colors focus:ring-2 focus:ring-[var(--accent-primary)]/30 focus:outline-none cursor-pointer group-hover:border-[var(--text-muted)]"
                    />
                    <svg className={`absolute w-3.5 h-3.5 text-white pointer-events-none transition-opacity ${checked ? 'opacity-100' : 'opacity-0'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                </div>
            </div>
            <div className="ml-3">
                <p className={`text-sm font-medium transition-colors ${checked ? 'text-[var(--text-muted)] line-through' : 'text-[var(--text-main)] group-hover:text-[var(--text-main)]'}`}>
                    {label}
                </p>
                {description && (
                    <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
                        {description}
                    </p>
                )}
            </div>
        </label>
    );
};

const RichMessageRenderer = ({ message, isMe, chatId }) => {
    // 0. System Message Override
    if (message.type === 'system') {
        return (
            <div className="w-full my-1.5 flex items-center opacity-80">
                <span className="font-mono text-[var(--text-muted)] text-xs">
                    {'>'} {message.text}
                </span>
            </div>
        );
    }

    // 1. Render the Text Wrapper (The Conversation)
    const renderText = () => (
        <div className="prose prose-sm prose-invert max-w-none text-sm leading-relaxed break-words
            prose-p:my-1.5 prose-p:first:mt-0 prose-p:last:mb-0
            prose-a:text-[var(--accent-primary)] prose-a:underline hover:prose-a:text-[var(--accent-hover)]
            prose-ul:my-1.5 prose-ul:pl-5 
            prose-ol:my-1.5 prose-ol:pl-5 
            prose-li:my-0.5
            prose-pre:bg-[var(--bg-tertiary)]/50 prose-pre:border prose-pre:border-[var(--border-subtle)]/50
            prose-code:text-[var(--accent-primary)] prose-code:bg-[var(--bg-tertiary)]/50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:before:hidden prose-code:after:hidden
            prose-headings:font-bold prose-headings:text-[var(--text-main)] prose-headings:mb-2 prose-headings:mt-3"
        >
            <ReactMarkdown>{message.text}</ReactMarkdown>
        </div>
    );

    // 2. Render the Action Payload (The Tool)
    const renderAction = () => {
        if (!message.action) return null;

        const { type, data } = message.action;

        switch (type) {
            case 'poll':
                return <PollComponent data={data} messageId={message.id} chatId={chatId} />;
            case 'table':
                return <TableComponent data={data} />;
            case 'map':
                return <MapComponent data={data} />;
            case 'calendar':
                return (
                    <div className="mt-2 p-4 bg-sky-900/20 border border-sky-800/50 rounded-2xl max-w-sm">
                        <p className="text-xs font-bold text-sky-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" /></svg>
                            Calendar Event Proposal
                        </p>
                        <h4 className="text-base font-bold text-[var(--text-main)] mb-1">{data.title}</h4>
                        <p className="text-sm font-medium text-sky-200/70">{data.start} - {data.end}</p>
                    </div>
                );
            case 'checklist':
                return (
                    <div className="mt-2 p-4 bg-[var(--bg-secondary)] border border-[var(--border-subtle)]/50 rounded-2xl min-w-[250px] shadow-sm flex-1">
                        <h4 className="text-sm font-bold text-[var(--text-main)] mb-3 pb-2 border-b border-[var(--border-subtle)]/50">{data.title}</h4>
                        <div className="space-y-1">
                            {data.items?.map((item, index) => (
                                <ChecklistItem key={index} item={item} />
                            ))}
                        </div>
                    </div>
                );
            case 'card':
                const cards = Array.isArray(data) ? data : [data];

                return (
                    <div className="flex gap-4 overflow-x-auto py-2 max-w-full custom-scrollbar scrollbar-hide snap-x">
                        {cards.map((card, index) => (
                            <div key={index} className="flex-shrink-0 w-64 sm:w-[280px] bg-[var(--bg-secondary)] border border-[var(--border-subtle)]/50 rounded-2xl overflow-hidden shadow-md snap-start">
                                {card.image && (
                                    <div
                                        className="w-full h-40 bg-black bg-cover bg-center bg-no-repeat border-b border-[var(--border-subtle)]/50"
                                        style={{ backgroundImage: `url("${card.image}")` }}
                                        role="img"
                                        aria-label={card.title}
                                    />
                                )}
                                <div className="p-4">
                                    <h4 className="font-bold text-[var(--text-main)] truncate text-[15px] mb-1 leading-snug" title={card.title}>{card.title}</h4>
                                    <p className="text-xs text-[var(--text-muted)] truncate leading-relaxed">{card.subtitle}</p>
                                    {card.link && (
                                        <a href={card.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-[var(--accent-primary)] hover:text-[var(--accent-hover)] transition-colors">
                                            View Details
                                            <svg className="w-4 h-4" transform="rotate(-45)" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            case 'text':
                return null;
            case 'desktop_response':
                return (
                    <div className="mt-2 p-3 bg-black/60 border border-green-500/20 rounded-xl overflow-x-auto max-w-full custom-scrollbar backdrop-blur-sm shadow-inner">
                        <pre className="m-0 font-mono text-[11px] sm:text-xs text-green-400 leading-relaxed whitespace-pre font-medium">
                            {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
                        </pre>
                    </div>
                );
            case 'vibe_result':
                return <VibeResultCard data={data} chatId={chatId} />;
            default:
                return null;
        }
    };

    return (
        <div className={`flex flex-col gap-1.5 min-w-0 w-full mb-1 group ${isMe ? 'items-end' : 'items-start'}`}>
            {/* The Text Content */}
            <div className="w-full">
                {renderText()}
            </div>

            {/* The Tool Card */}
            {message.action && (
                <div className={`w-full max-w-[85%] sm:max-w-[420px] overflow-hidden ${isMe ? 'flex justify-end' : 'flex justify-start'}`}>
                    {renderAction()}
                </div>
            )}
        </div>
    );
};

export default RichMessageRenderer;
